<template>
  <div ref="bg">
    <!-- Capa e detalhes -->
    <div class="mb-4 d-flex flex-column align-center">
      <div style="max-width: 700px; width: 100%">
        <party-cover-video :party="data?.party" />
      </div>
    </div>
    <div class="d-flex flex-column" style="gap: 1rem">
      <v-card class="rounded-lg" elevation="0">
        <v-card-title class="">
          <div class="flex-grow-1">
            <v-chip
              v-if="!loading"
              label
              small
              @click="goTo({ to: 'policies' })"
              :color="data?.party.ageGroup == 0 ? 'success' : 'info'"
              class="float-right ml-2 mt-1"
            >
              {{
                data?.party.ageGroup == 0 ? "Livre" : `+${data?.party.ageGroup}`
              }}
              {{ data?.party.ageWithParent ? "*" : "" }}
            </v-chip>
            <h3 class="mb-0" style="word-break: normal; line-height: 1.2">
              <v-skeleton-loader
                v-if="loading"
                type="heading"
                height="30"
                width="75%"
              />
              <span v-else>
                {{ data?.party.name }}
              </span>
            </h3>

            <h6
              v-if="loading || data?.party?.subtitle"
              class="mb-0 flex-grow-1 text--secondary"
              style="word-break: normal; line-height: 1.2"
            >
              <v-skeleton-loader
                v-if="loading"
                type="heading"
                height="17"
                width="60%"
              />
              <span v-else>{{ data?.party.subtitle }}</span>
            </h6>
          </div>
        </v-card-title>
        <v-card-text>
          <div class="d-flex flex-column gap-1">
            <div class="d-flex align-center">
              <v-icon small left>mdi-calendar-start</v-icon>
              <v-skeleton-loader
                class="flex-grow-1 mb-0 py-1"
                v-if="loading"
                type="heading"
                height="22"
              />
              <span v-else>{{ data?.party.date | formatDate }} até</span>
            </div>
            <div
              class="d-flex align-center"
              v-if="data?.party?.endDate || loading"
            >
              <v-icon small left>mdi-calendar-end</v-icon>
              <v-skeleton-loader
                class="flex-grow-1 mb-0 py-1"
                v-if="loading"
                type="heading"
                height="22"
              />
              <span v-else>{{ data?.party.endDate | formatDate }}</span>
            </div>
          </div>
          <v-card
            outlined
            v-if="data?.party?.description"
            class="mt-2 pa-3 pb-1"
          >
            <h6 class="mb-0">Descrição</h6>
            <party-description-card :lines="15" :party="data?.party" />
          </v-card>
        </v-card-text>
      </v-card>
      <!-- ParentParty -->
      <v-card
        v-if="data?.party?.ParentParty"
        :to="{
          name: 'shop.party',
          params: {
            orgSlug: data?.organization?.slug,
            partyId:
              data?.party?.ParentParty?.slug || data?.party?.ParentParty?.id,
          },
        }"
        class="rounded-lg"
        elevation="0"
      >
        <div class="d-flex align-center gap-3">
          <div>
            <v-img
              :src="data?.party?.ParentParty?.cover"
              :alt="data?.party?.ParentParty?.name"
              height="60px"
              :width="(60 * 16) / 9"
              :aspect-ratio="16 / 9"
              class="rounded-l-lg"
            />
          </div>
          <div class="flex-grow-1">
            <p class="mb-0 lh-1">Esse evento faz parte de</p>
            <h6 class="mb-0 lh-1">{{ data?.party?.ParentParty?.name }}</h6>
          </div>
          <v-icon>mdi-chevron-right</v-icon>
        </div>
      </v-card>
      <!-- Status -->
      <template v-if="status === true && !loading">
        <banner-viewer
          v-if="banners.before_tickets"
          :banner="banners.before_tickets"
          :aspect-ratio="8 / 1"
          :image-url="banners.before_tickets.imageUrl"
          :link="banners.before_tickets.link"
          :isSafetyLink="banners.before_tickets.isSafetyLink"
        />
      </template>
      <v-alert
        v-else
        text
        type="warning"
        class="mb-0"
        :icon="status == 'finished' ? 'mdi-calendar-remove' : undefined"
      >
        <b v-if="status == 'finished'">Este evento já terminou</b>
        <template v-else-if="status == 'cancelled'">
          <b>Esse evento foi cancelado.</b><br />
          {{ data?.party.cancelledReason }}
        </template>
        <b v-else>Esse evento está indisponível</b>
      </v-alert>

      <banner-viewer
        v-if="banners.after_tickets"
        :banner="banners.after_tickets"
        :aspect-ratio="9 / 2"
        :image-url="banners.after_tickets.imageUrl"
        :link="banners.after_tickets.link"
        :isSafetyLink="banners.after_tickets.isSafetyLink"
      />

      <party-address :party="data?.party" :loading="loading" />

      <organization-card
        ref="organization"
        :loading="loading"
        :organization="data?.organization"
      />

      <spotify-embed v-if="data?.party.spotifyId" :id="data?.party.spotifyId" />
      <!-- MAP -->
      <party-map v-if="data?.party?.imageMap" :data="data" />
      <GLTFMapViewer v-if="has3DMap" ref="3dMap" :map="data?.map" />

      <banner-viewer
        v-if="banners.sidebar"
        :banner="banners.sidebar"
        :aspect-ratio="4 / 3"
        :image-url="banners.sidebar.imageUrl"
        :link="banners.sidebar.link"
        :isSafetyLink="banners.sidebar.isSafetyLink"
      />
      <party-gallery ref="gallery" v-if="hasGallery" :gallery="data?.gallery" />
      <attractions-list
        ref="attractions"
        v-if="hasAttractions"
        :attractions="data?.attractions"
        :periods="data?.periods"
        :tickets="data?.tickets"
      />

      <faq-viewer ref="faqs" v-if="hasFaq" :data="data?.faqs" viewOnly />

      <party-policies :party="data?.party" :loading="loading" ref="policies" />
    </div>
  </div>
</template>

<script>
import moment from "moment";
import PartyStatus from "@/utils/partyStatus";
import PartyDescriptionCard from "@/components/shop/PartyDescriptionCard.vue";
import PartyGallery from "@/components/shop/PartyGallery.vue";
import AttractionsList from "@/components/global/party/AttractionsList.vue";
import OrganizationCard from "@/components/shop/OrganizationCard.vue";
import PartyAddress from "@/components/shop/PartyAddress.vue";
import PartyMap from "@/components/shop/PartyMap.vue";
import PartyCoverVideo from "@/components/shop/PartyCoverVideo.vue";
import PartyPolicies from "@/components/shop/PartyPolicies.vue";
import GLTFMapViewer from "@/components/shop/GLTFMapViewer.vue";
import SpotifyEmbed from "@/components/global/SpotifyEmbed.vue";
import FaqViewer from "@/components/admin/party/FAQViewer.vue";
import BannerViewer from "@/components/global/BannerViewer.vue";

export default {
  components: {
    PartyDescriptionCard,
    PartyGallery,
    AttractionsList,
    OrganizationCard,
    PartyAddress,
    PartyMap,
    PartyPolicies,
    PartyCoverVideo,
    GLTFMapViewer,
    SpotifyEmbed,
    FaqViewer,
    BannerViewer,
  },
  data() {
    return {
      fLinks: [
        {
          icon: "mdi-share-variant",
          label: "Compartilhar",
          func: this.share,
          if: "canShare",
        },
        {
          label: "Organização",
          to: "organization",
        },
        {
          label: "Mapa",
          to: "tableMap",
          if: "hasTableMap",
        },
        {
          label: "Mapa 3D",
          to: "3dMap",
          if: "has3DMap",
        },
        {
          label: "Galeria",
          to: "gallery",
          if: "hasGallery",
        },
        {
          label: "Cronograma",
          to: "attractions",
          if: "hasAttractions",
        },
        {
          label: "Dúvidas",
          to: "faqs",
          if: "hasFaq",
        },
      ],
    };
  },
  methods: {
    share() {
      navigator.share({
        title: this.data?.party.name,
        text: this.data?.party.name,
        url: window.location.href,
      });
    },
    goTo({ to, func }) {
      if (func) return func();

      const el = this.$refs[to];

      this.$vuetify.goTo(el || to, {
        offset: 30,
        behavior: "smooth",
      });
    },
    setBg() {
      this.$root.$emit("setImgBg", this.data?.party.cover);
    },
  },
  created() {},
  mounted() {
    this.setBg();
  },
  destroyed() {
    this.$root.$emit("setImgBg", null);
  },
  watch: {
    "data.party.cover": {
      handler() {
        this.setBg();
      },
    },
  },
  computed: {
    status() {
      const party = this.data?.baseParty || this.data?.party;
      if (!party) return false;
      const status = new PartyStatus(party);
      return status.isActive || status.status;
    },
    canShare() {
      return navigator.share && this.isPublic;
    },
    isPublic() {
      return !this.data?.party?.private;
    },
    userHasTicket() {
      return this.data?.tickets && this.data?.tickets.length > 0;
    },
    hasGallery() {
      return this.data?.gallery && this.data?.gallery.length > 0;
    },
    hasAttractions() {
      return this.data?.attractions && this.data?.attractions.length > 0;
    },
    hasFaq() {
      return this.data?.faqs && this.data?.faqs.length > 0;
    },
    hasTableMap() {
      return this.data?.tableMap;
    },
    has3DMap() {
      return this.data?.map && this.data?.map;
    },
    fastLinks() {
      return this.fLinks.filter((link) => {
        if (link.if) return this[link.if];
        return true;
      });
    },
    banners() {
      const bannerMap = {
        before_tickets: null,
        after_tickets: null,
        sidebar: null,
      };

      if (this.data?.banners) {
        this.data.banners.forEach((banner) => {
          if (banner.position in bannerMap) {
            bannerMap[banner.position] = banner;
          }
        });
      }

      return bannerMap;
    },
  },
  filters: {
    formatDate(date) {
      const momentDate = moment(date);
      var formatPattern = "DD [de] MMMM";

      if (momentDate.year() != moment().year()) formatPattern += " [de] YYYY";
      else if (momentDate.isBefore(moment(), "day"))
        formatPattern += " [de] YYYY";

      formatPattern += " [às] HH[h]mm";

      return momentDate.format(formatPattern);
    },
  },
  props: {
    data: {
      type: Object,
    },
    code: {
      type: String,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style></style>
